import React, { type ReactNode, Suspense, useMemo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';

import {
	EditionAwarenessContext,
	type EditionAwarenessContextType,
} from '../services/edition-awareness-context';
import { getTrialDaysLeft } from '../services/get-trial-days-left';
import { getHamsKey } from '../services/product-to-hams-key';
import { useErrorAnalytics } from '../services/use-analytics';
import type { Edition, Product } from '../types';

import type { editionAwarenessQuery } from './__generated__/editionAwarenessQuery.graphql';
import { AudienceAndTiming } from './audience-and-timing';

export type EditionAwarenessProps = {
	cloudId: string;
	product: Product;
	edition: Edition;
	freeButton: ReactNode;
	trialButton: ReactNode;
};

/**
 * Edition awareness is used to communicate to customers about the current edition they have and if they should
 * - Add payment details
 * - Upgrade their edition
 *
 * This component deals with creating the context which is then used throughout the package to:
 * - Track analytics
 * - Know which product/edition/cloud id is being used
 * - Know when to show/hide the button
 */
function EditionAwarenessImpl({ freeButton, trialButton, ...props }: EditionAwarenessProps) {
	const { edition, product, cloudId } = props;

	const data = useLazyLoadQuery<editionAwarenessQuery>(
		graphql`
			query editionAwarenessQuery(
				$cloudId: ID!
				$productKey: String!
				$shouldCheckForFree: Boolean!
				$shouldCheckForTrial: Boolean!
				$shouldSkip: Boolean!
			) {
				tenantContexts(cloudIds: [$cloudId])
					@skip(if: $shouldSkip)
					@optIn(to: ["CcpAllUserUpgradeAndPay", "HamsAllUserUpgradeAndPay"]) {
					entitlementInfo(hamsProductKey: $productKey) {
						freeEntitlement: entitlement @include(if: $shouldCheckForFree) {
							experienceCapabilities {
								changeOfferingV2(offeringName: "Standard") {
									isAvailableToUser
									experienceUrl
								}
							}
						}

						trialEntitlement: entitlement(where: { AND: [{ inTrialOrPreDunning: true }] })
							@include(if: $shouldCheckForTrial) {
							preDunning @include(if: $shouldCheckForTrial) {
								status
							}
							subscription {
								trial {
									endTimestamp
								}
								accountDetails {
									invoiceGroup {
										invoiceable
										experienceCapabilities {
											configurePaymentV2 {
												isAvailableToUser
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			shouldSkip: product === 'jira-product-discovery' && fg('jpd_disable_edition_awareness'),
			shouldCheckForFree: edition === 'free',
			shouldCheckForTrial: edition === 'premium' || edition === 'standard',
			cloudId,
			productKey: getHamsKey(product),
		},
	);

	const freeEntitlement = data?.tenantContexts?.[0]?.entitlementInfo?.freeEntitlement;
	const trialEntitlement = data?.tenantContexts?.[0]?.entitlementInfo?.trialEntitlement;

	// We treat null and true as invoiceable, this is to prevent showing the customer the experience
	// when they may already have payment details on file.
	const isInvoiceable =
		trialEntitlement?.subscription?.accountDetails?.invoiceGroup?.invoiceable !== false;

	const canAddPaymentDetails =
		trialEntitlement?.subscription?.accountDetails?.invoiceGroup?.experienceCapabilities
			?.configurePaymentV2?.isAvailableToUser === true;

	const trialDaysLeft = getTrialDaysLeft(trialEntitlement?.subscription?.trial?.endTimestamp);
	const isInTrial = Boolean(trialDaysLeft && trialDaysLeft > 0);

	const isInPredunning =
		trialEntitlement?.preDunning?.status === 'IN_PRE_DUNNING' && !isInvoiceable;

	const isChangeOfferingAvailable =
		freeEntitlement?.experienceCapabilities?.changeOfferingV2?.isAvailableToUser;
	const changeOfferingExperienceUrl =
		freeEntitlement?.experienceCapabilities?.changeOfferingV2?.experienceUrl;
	const canUpgrade = Boolean(isChangeOfferingAvailable && changeOfferingExperienceUrl);

	const context: EditionAwarenessContextType = useMemo(() => {
		return {
			cloudId,
			product,
			edition,
			isInvoiceable,
			canAddPaymentDetails,
			trialDaysLeft,
			isInTrial,
			isInPredunning,
			canUpgrade,
		};
	}, [
		cloudId,
		product,
		edition,
		isInvoiceable,
		canAddPaymentDetails,
		trialDaysLeft,
		isInTrial,
		isInPredunning,
		canUpgrade,
	]);

	return (
		<EditionAwarenessContext.Provider value={context}>
			<AudienceAndTiming freeButton={freeButton} trialButton={trialButton} />
		</EditionAwarenessContext.Provider>
	);
}

export function EditionAwareness(props: EditionAwarenessProps) {
	const handleOnError = useErrorAnalytics({ product: props.product, edition: props.edition });

	return (
		<ErrorBoundary fallback={null} onError={handleOnError}>
			<Suspense fallback={null}>
				<EditionAwarenessImpl {...props} />
			</Suspense>
		</ErrorBoundary>
	);
}
